import deepEqual from "fast-deep-equal";
import type { StateAction } from "@entrilia/shared/types";
import { generateGuid } from "@entrilia/shared/utilities/generateGuid";
import type { ValidationResult } from "@entrilia/shared/utilities/validators";
import {
  combineValidators,
  invalidResult,
  maxCharactersValidator,
  nonEmptyArrayValidator,
  requiredValidator,
  validResult,
} from "@entrilia/shared/utilities/validators";
import type { ObjectChecklist, ObjectChecklistCondition } from "../../../../api/types/objectTypes";
import { validateObjectFilter } from "../../../object-filters/objectFilterValidator";

export interface ChecklistConfigurationState {
  view: "list" | "add" | "edit";
  editedChecklistId?: string;
  form: ChecklistEditForm;
  // Client-side row keys for form.conditions; kept outside the form so dirty checks compare API data only.
  conditionKeys: string[];
}

export interface ChecklistEditForm {
  name: string;
  taskTemplateIds: string[];
  conditions: ObjectChecklistCondition[];
}

export interface ConditionValidation {
  overall: ValidationResult;
  filters: ValidationResult[];
}

export interface ChecklistEditFormValidation {
  isValid: boolean;
  name: ValidationResult;
  taskTemplateIds: ValidationResult;
  condition: ConditionValidation;
}

export const getInitialChecklistConfigurationState = (): ChecklistConfigurationState => ({
  view: "list",
  form: {
    name: "",
    taskTemplateIds: [],
    conditions: [],
  },
  conditionKeys: [],
});

const createConditionKeys = (conditions: ObjectChecklistCondition[]): string[] => conditions.map(() => generateGuid());

const checklistNameValidator = combineValidators(requiredValidator, maxCharactersValidator(50));
const checklistTasksValidator = nonEmptyArrayValidator("At least one task is required");

export const validateChecklistEditForm = (form: ChecklistEditForm): ChecklistEditFormValidation => {
  const name = checklistNameValidator(form.name);
  const taskTemplateIds = checklistTasksValidator(form.taskTemplateIds);
  const conditionFilters = form.conditions.map((c) => validateObjectFilter(c.field));
  const condition: ConditionValidation = {
    overall: conditionFilters.every((f) => f.isValid)
      ? validResult()
      : invalidResult("All conditions must be fully configured"),
    filters: conditionFilters,
  };
  return {
    isValid: name.isValid && taskTemplateIds.isValid && condition.overall.isValid,
    name,
    taskTemplateIds,
    condition,
  };
};

export const initialChecklistEditFormValidation: ChecklistEditFormValidation = {
  isValid: true,
  name: validResult(),
  taskTemplateIds: validResult(),
  condition: { overall: validResult(), filters: [] },
};

export const isChecklistFormDirty = (form: ChecklistEditForm, storedChecklist: ObjectChecklist): boolean =>
  form.name !== storedChecklist.name ||
  form.taskTemplateIds.join(",") !== storedChecklist.taskTemplateIds.join(",") ||
  !deepEqual(form.conditions, storedChecklist.conditions);

// Actions

export const startAddingChecklistAction = (): StateAction<ChecklistConfigurationState> => (state) => ({
  ...state,
  view: "add",
  editedChecklistId: undefined,
  form: {
    name: "",
    taskTemplateIds: [],
    conditions: [],
  },
  conditionKeys: [],
});

export const startEditingChecklistAction =
  (checklist: ObjectChecklist): StateAction<ChecklistConfigurationState> =>
  (state) => ({
    ...state,
    view: "edit",
    editedChecklistId: checklist.id,
    form: {
      name: checklist.name,
      taskTemplateIds: checklist.taskTemplateIds,
      conditions: checklist.conditions,
    },
    conditionKeys: createConditionKeys(checklist.conditions),
  });

export const changeChecklistNameAction =
  (name: string): StateAction<ChecklistConfigurationState> =>
  (state) => ({
    ...state,
    form: { ...state.form, name },
  });

export const resetChecklistFormAction =
  (checklist: ObjectChecklist): StateAction<ChecklistConfigurationState> =>
  (state) => ({
    ...state,
    view: "edit",
    form: {
      name: checklist.name,
      taskTemplateIds: checklist.taskTemplateIds,
      conditions: checklist.conditions,
    },
    conditionKeys: createConditionKeys(checklist.conditions),
  });

export const goBackToListAction = (): StateAction<ChecklistConfigurationState> => (state) => ({
  ...state,
  view: "list",
  editedChecklistId: undefined,
  form: {
    name: "",
    taskTemplateIds: [],
    conditions: [],
  },
  conditionKeys: [],
});

export const addTaskAction =
  (taskTemplateId: string): StateAction<ChecklistConfigurationState> =>
  (state) => ({
    ...state,
    form: { ...state.form, taskTemplateIds: [...state.form.taskTemplateIds, taskTemplateId] },
  });

export const removeTaskAction =
  (taskTemplateId: string): StateAction<ChecklistConfigurationState> =>
  (state) => ({
    ...state,
    form: { ...state.form, taskTemplateIds: state.form.taskTemplateIds.filter((id) => id !== taskTemplateId) },
  });

export const moveTaskAction =
  (index: number, direction: "up" | "down"): StateAction<ChecklistConfigurationState> =>
  (state) => {
    const ids = [...state.form.taskTemplateIds];
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (index < 0 || index >= ids.length || swapIndex < 0 || swapIndex >= ids.length) {
      return state;
    }
    [ids[index], ids[swapIndex]] = [ids[swapIndex] as string, ids[index] as string];
    return { ...state, form: { ...state.form, taskTemplateIds: ids } };
  };

export const addConditionFilterAction = (): StateAction<ChecklistConfigurationState> => (state) => ({
  ...state,
  form: {
    ...state.form,
    conditions: [
      ...state.form.conditions,
      { objectType: "", field: { fieldId: "", textFilter: { operator: "Contains" } } },
    ],
  },
  conditionKeys: [...state.conditionKeys, generateGuid()],
});

export const removeConditionFilterAction =
  (index: number): StateAction<ChecklistConfigurationState> =>
  (state) => ({
    ...state,
    form: {
      ...state.form,
      conditions: state.form.conditions.filter((_, i) => i !== index),
    },
    conditionKeys: state.conditionKeys.filter((_, i) => i !== index),
  });

export const setConditionAction =
  (index: number, condition: ObjectChecklistCondition): StateAction<ChecklistConfigurationState> =>
  (state) => {
    const conditions = [...state.form.conditions];
    if (index < 0 || index >= conditions.length) {
      return state;
    }
    conditions[index] = condition;
    return { ...state, form: { ...state.form, conditions } };
  };
